import React, { useEffect, useRef, useState } from 'react';
import {
  AUTOMATED_PAYMENT_COMPLETED_NOTIF_TYPE,
  AUTOMATED_PAYMENT_FAILED_NOTIF_TYPE,
  AUTOMATED_SHIPMENT_COMPLETED_NOTIF_TYPE,
  BULK_ORDER_PROCESSING_FAILURE,
  BULK_UPDATE_COUPON_NOTIF_TYPE,
  CONTENT_SUBMITTED_NOTIF_TYPE,
  EMAIL_ACCOUNT_UPDATE_FAILURE_NOTIF_TYPE,
} from '@Saral-Influencer-Marketing/saral-utils/src/constants/notification';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Mixpanel } from '../../../../src/utils/mixpanel';
import { fetchNotifications } from '../../../redux/notifications/notificationActions';
import { updateSeenStatus } from '../../../services/notifications.service';
import NotificationSkeleton from '../../Skeletons/NotificationSkeleton';
import AddressSavedNotification from './AddressSavedNotification';
import AutomatedPaymentNotification from './AutomatedPaymentNotification';
import BulkCouponUpdateNotification from './BulkCouponUpdateNotification';
import BulkOrderPlacementNotification from './BulkOrderPlacementNotification';
import CommissionTierRevertedNotification from './CommissionTierRevertedNotification';
import ContentSubmissionNotification from './ContentSubmissionNotification';
import DemographicsNotifcation from './DemographicsNotifcation';
import EmailAccountUpdateFailureNotification from './EmailAccountUpdateFailureNotification';
import EmailDisconnectNotification from './EmailDisconnectNotification';
import EnrichCompletedNotification from './EnrichCompletedNotification';
import LogPostNotification from './LogPostNotification';
import PackagesNotification from './PackagesNotification';
import PostTrackingNotification from './PostTrackingNotification';
import RatnaNotification from './RatnaNotification';
import ReachoutLimitNotification from './ReachoutLimitNotification';
import RefreshStatsNotification from './RefreshStatsNotification';
import ShipmentNotification from './ShipmentNotification';
import TaskNotification from './TaskNotification';
import UsageRightNotification from './UsageRightNotification';

// Notification Wrapper Component
const NotificationWrapper = ({ notification, notificationRef, children }) => {
  const bgColor = notification?.seen ? 'var(--white)' : 'var(--saral-purple-10)';

  return (
    <div ref={notificationRef} style={{ background: bgColor }}>
      {children}
    </div>
  );
};

export default function SingleNotification({
  notification,
  setSelectedInfluencer,
  setShowDemographics,
  setShowNotificationDropdown,
  setShowLitePlanModal,
  setDemographicsId,
  notificationIds,
  setNotificationIds,
}) {
  const [isSeen, setIsSeen] = useState(notification?.seen);
  const navigate = useNavigate();
  const notificationRef = useRef();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);

  const markSingleNotificationAsSeen = async () => {
    const response = await updateSeenStatus({
      notificationIds: [notification._id],
    });
    if (response?.status) {
      dispatch(fetchNotifications());
      setNotificationIds([]);
    }
  };

  const handleNavigate = () => {
    const redirectUrl = `/dashboard/profile/${notification?.influencerId?.lists[0]}/${notification?.influencerId._id}`;
    if (redirectUrl === location.pathname) {
      setShowNotificationDropdown(null);
    } else {
      navigate(redirectUrl);
    }
    markSingleNotificationAsSeen();
  };

  const handleNavigateList = () => {
    const redirectUrl = `/dashboard/list/detail/${notification?.content?.listId}`;

    navigate(redirectUrl);
    setShowNotificationDropdown(null);

    markSingleNotificationAsSeen();
  };

  const handleViewDemographics = (content) => {
    Mixpanel.identify(userDetails?._id);
    setShowDemographics(true);
    setSelectedInfluencer({
      socialId: content?.socialId,
      username: content?.username,
      profileLink: content?.profileLink,
      platform: content?.platform,
    });
    setDemographicsId(content?.demographicId);
    setShowNotificationDropdown(null);
    markSingleNotificationAsSeen();
  };

  // https://getsaral.atlassian.net/wiki/spaces/SD/pages/110362625/Notifications+redesign+-+QA+doc?focusedCommentId=111083568
  // IF we want to mark as seen for viewed notification

  // useEffect(() => {
  //   const observeElement = notificationRef.current;

  //   if (!observeElement) {
  //     console.error('Element to observe is not available.');
  //     return;
  //   }

  //   const observer = new IntersectionObserver(([entry]) => {
  //     setIsSeen(entry.isIntersecting);
  //   });

  //   observer.observe(observeElement);

  //   return () => {
  //     if (observeElement) {
  //       observer.unobserve(observeElement);
  //     }
  //     observer.disconnect(); // Fully clean up the observer
  //   };
  // }, []);

  useEffect(() => {
    if (isSeen && !notification.seen) {
      if (!notificationIds.includes(notification._id)) {
        setNotificationIds([...notificationIds, notification._id]);
      }
    }
  }, [isSeen]);

  if (!notification) {
    return <NotificationSkeleton />;
  }

  if (notification.type === CONTENT_SUBMITTED_NOTIF_TYPE) {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <ContentSubmissionNotification
          notification={notification}
          handleNavigate={handleNavigate}
          setShowNotificationDropdown={setShowNotificationDropdown}
        />
      </NotificationWrapper>
    );
  }

  if (notification.type === BULK_ORDER_PROCESSING_FAILURE) {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <BulkOrderPlacementNotification
          notification={notification}
          setShowNotificationDropdown={setShowNotificationDropdown}
        />
      </NotificationWrapper>
    );
  }

  if (notification.type === AUTOMATED_SHIPMENT_COMPLETED_NOTIF_TYPE) {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <ShipmentNotification notification={notification} handleNavigate={handleNavigate} />
      </NotificationWrapper>
    );
  }

  if (notification.type === 'task') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <TaskNotification notification={notification} handleNavigate={handleNavigate} />
      </NotificationWrapper>
    );
  } else if (notification.type === 'demographics') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <DemographicsNotifcation notification={notification} handleViewDemographics={handleViewDemographics} />
      </NotificationWrapper>
    );
  } else if (notification.type === 'package') {
    // DEPRECATED - This is not in use last package was created at june 2023
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <PackagesNotification notification={notification} handleNavigate={handleNavigate} />
      </NotificationWrapper>
    );
  } else if (notification.type === 'freeCreditRatnas') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <RatnaNotification notification={notification} />
      </NotificationWrapper>
    );
  } else if (notification?.title === 'Commission Tier Updated') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <CommissionTierRevertedNotification notification={notification} />
      </NotificationWrapper>
    );
  } else if (notification.type === 'addressSaved') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <AddressSavedNotification notification={notification} handleNavigate={handleNavigate} />
      </NotificationWrapper>
    );
  } else if (notification.type === 'logPost') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <LogPostNotification notification={notification} handleNavigate={handleNavigate} />
      </NotificationWrapper>
    );
  } else if (notification.type === 'postTracking') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <PostTrackingNotification
          notification={notification}
          setShowNotificationDropdown={setShowNotificationDropdown}
          markSingleNotificationAsSeen={markSingleNotificationAsSeen}
        />
      </NotificationWrapper>
    );
  } else if (notification.type === 'usageRight') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <UsageRightNotification notification={notification} />
      </NotificationWrapper>
    );
  } else if (notification.type === 'emailDisconnect') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <EmailDisconnectNotification notification={notification} />
      </NotificationWrapper>
    );
  } else if (notification.type === 'reachoutLimit') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <ReachoutLimitNotification
          notification={notification}
          setShowLitePlanModal={setShowLitePlanModal}
          setShowNotificationDropdown={setShowNotificationDropdown}
          markSingleNotificationAsSeen={markSingleNotificationAsSeen}
        />
      </NotificationWrapper>
    );
  } else if (notification?.type === 'enrichList') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <EnrichCompletedNotification notification={notification} handleNavigateList={handleNavigateList} />
      </NotificationWrapper>
    );
  } else if (notification?.type === 'refreshStats') {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <RefreshStatsNotification notification={notification} />
      </NotificationWrapper>
    );
  } else if (notification?.type === EMAIL_ACCOUNT_UPDATE_FAILURE_NOTIF_TYPE) {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <EmailAccountUpdateFailureNotification
          body={notification?.content?.body || ''}
          email={notification?.content?.email}
          seen={notification?.seen}
          createdAt={notification?.createdAt}
          handleSeen={markSingleNotificationAsSeen}
        />
      </NotificationWrapper>
    );
  } else if (notification.type === BULK_UPDATE_COUPON_NOTIF_TYPE) {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <BulkCouponUpdateNotification notification={notification} />
      </NotificationWrapper>
    );
  } else if (
    notification.type === AUTOMATED_PAYMENT_FAILED_NOTIF_TYPE ||
    notification.type === AUTOMATED_PAYMENT_COMPLETED_NOTIF_TYPE
  ) {
    return (
      <NotificationWrapper notification={notification} notificationRef={notificationRef}>
        <AutomatedPaymentNotification
          notification={notification}
          setShowNotificationDropdown={setShowNotificationDropdown}
        />
      </NotificationWrapper>
    );
  } else return null;
}
